import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, ArrowLeft, ShieldCheck, Layers, Zap, MapPinned, Wallet, Eye } from "lucide-react";

/**
 * Phase 5 — Super Admin Permissions Editor.
 * Route: /users/permissions/:id
 * Access: super_admin ONLY. Guarded here + at backend (GET/PATCH /users).
 *
 * Data model mapping (what this page saves → what the backend stores):
 *   role                      → user.role
 *   modules[]                 → user.modules
 *   actions[]                 → user.actions
 *   territory.districts[]     → user.territory.districts
 *   territory.distributor_ids → user.territory.distributor_ids (UI hook only — list not yet sourced)
 *   price_region_id           → user.price_region_id
 *   commercial_visibility{4}  → user.commercial_visibility
 *
 * Validation:
 *   • No changes saved without explicit "Save Changes" click (no auto-save).
 *   • Super admin cannot remove own manage_users action or disable self (client-side guard).
 *   • Backend validates module/action keys (400 on unknown).
 */
export default function UserPermissions() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taxonomy, setTaxonomy] = useState(null);
  const [target, setTarget] = useState(null);      // the user being edited
  const [original, setOriginal] = useState(null);  // for dirty-check
  // Form state
  const [role, setRole] = useState("sales");
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [priceRegion, setPriceRegion] = useState("");
  const [cv, setCv] = useState({ show_prices: true, show_totals: true, show_gst: true, show_payment_status: true });

  const notSuperAdmin = !!(me && me.role !== "super_admin");

  // Load user + taxonomy in parallel
  useEffect(() => {
    if (notSuperAdmin) { setLoading(false); return; }
    (async () => {
      try {
        const [uRes, tRes] = await Promise.all([api.get("/users"), api.get("/permissions/taxonomy")]);
        const u = uRes.data.find((x) => x.id === id);
        if (!u) { toast.error("User not found"); nav("/users"); return; }
        setTaxonomy(tRes.data);
        setTarget(u);
        setOriginal(JSON.stringify({
          role: u.role,
          modules: u.modules || tRes.data.role_templates[u.role]?.modules || [],
          actions: u.actions || tRes.data.role_templates[u.role]?.actions || [],
          districts: u.territory?.districts || [],
          price_region_id: u.price_region_id || "",
          commercial_visibility: u.commercial_visibility || tRes.data.role_templates[u.role]?.commercial_visibility || {},
        }));
        setRole(u.role);
        setModules(u.modules || tRes.data.role_templates[u.role]?.modules || []);
        setActions(u.actions || tRes.data.role_templates[u.role]?.actions || []);
        setDistricts(u.territory?.districts || []);
        setPriceRegion(u.price_region_id || "");
        setCv(u.commercial_visibility || tRes.data.role_templates[u.role]?.commercial_visibility || { show_prices: true, show_totals: true, show_gst: true, show_payment_status: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Changing role → offer to load template defaults (one-click reset)
  const applyTemplate = (newRole) => {
    const t = taxonomy?.role_templates?.[newRole];
    if (!t) return;
    setRole(newRole);
    setModules([...t.modules]);
    setActions([...t.actions]);
    setCv({ ...t.commercial_visibility });
    toast.info(`Loaded ${newRole} template defaults — you can still customise below.`);
  };

  const toggleMod = (k) => setModules((m) => m.includes(k) ? m.filter((x) => x !== k) : [...m, k]);
  const toggleAct = (k) => setActions((a) => a.includes(k) ? a.filter((x) => x !== k) : [...a, k]);
  const toggleDist = (name) => setDistricts((d) => d.includes(name) ? d.filter((x) => x !== name) : [...d, name]);

  const dirty = useMemo(() => {
    if (!original) return false;
    return original !== JSON.stringify({ role, modules, actions, districts, price_region_id: priceRegion, commercial_visibility: cv });
  }, [role, modules, actions, districts, priceRegion, cv, original]);

  // Safety guards — super_admin editing themselves cannot lock themselves out
  const editingSelf = me?.id === id;
  const selfGuardWarnings = useMemo(() => {
    const warnings = [];
    if (editingSelf) {
      if (role !== "super_admin") warnings.push("You are changing your own role away from super_admin — you will lose access to this page.");
      if (role === "super_admin" && (!modules.includes("users") || !actions.includes("manage_users"))) warnings.push("Removing Users/manage_users will take effect only for regular admins — super_admin retains implicit access.");
    }
    return warnings;
  }, [editingSelf, role, modules, actions]);

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/users/${id}`, {
        role,
        modules,
        actions,
        territory: { districts, distributor_ids: target?.territory?.distributor_ids || [] },
        price_region_id: priceRegion || null,
        commercial_visibility: cv,
      });
      toast.success("Permissions saved.");
      setOriginal(JSON.stringify({ role, modules, actions, districts, price_region_id: priceRegion, commercial_visibility: cv }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  if (notSuperAdmin) return <Navigate to="/" replace />;
  if (loading || !taxonomy || !target) return <div className="p-8 text-slate-500">Loading permission editor…</div>;

  return (
    <div className="space-y-6" data-testid="user-permissions-root">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="space-y-1">
          <button onClick={() => nav("/users")} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-users"><ArrowLeft className="h-3.5 w-3.5" /> Back to Users</button>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Permissions — {target.name}</h1>
          <p className="text-sm text-slate-500">{target.email} · <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700">{target.role}</span></p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/users")}>Cancel</Button>
          <Button onClick={save} disabled={!dirty || saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-permissions">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : dirty ? "Save Changes" : "No Changes"}
          </Button>
        </div>
      </div>

      {/* Warnings */}
      {selfGuardWarnings.length > 0 && (
        <Card className="p-3 border border-amber-200 bg-amber-50 rounded-xl">
          <div className="text-sm text-amber-900 space-y-1">{selfGuardWarnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</div>
        </Card>
      )}

      {/* Summary preview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border border-slate-200 rounded-xl" data-testid="summary-modules">
          <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase tracking-wider"><Layers className="h-3.5 w-3.5" /> Modules</div>
          <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{modules.length} <span className="text-sm text-slate-400 font-normal">/ {taxonomy.modules.length}</span></div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-xl" data-testid="summary-actions">
          <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase tracking-wider"><Zap className="h-3.5 w-3.5" /> Actions</div>
          <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{actions.length} <span className="text-sm text-slate-400 font-normal">/ {taxonomy.actions.length}</span></div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-xl" data-testid="summary-districts">
          <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase tracking-wider"><MapPinned className="h-3.5 w-3.5" /> Districts</div>
          <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{districts.length || <span className="text-slate-400 text-base font-normal">none — all</span>}</div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-xl" data-testid="summary-price-region">
          <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase tracking-wider"><Wallet className="h-3.5 w-3.5" /> Price List</div>
          <div className="text-sm font-bold text-slate-900 mt-1 truncate">{taxonomy.regions.find((r) => r.id === priceRegion)?.name || <span className="text-slate-400 font-normal">unset</span>}</div>
        </Card>
      </div>

      {/* Role template */}
      <Card className="p-5 border border-slate-200 rounded-xl">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-slate-700" /><h2 className="heading text-base font-bold text-slate-900">Role Template</h2></div>
            <p className="text-xs text-slate-500 mt-0.5">Base access profile. Switching role loads defaults — you can still override any toggle below.</p>
          </div>
          <div className="min-w-[220px]">
            <Select value={role} onValueChange={applyTemplate}>
              <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{taxonomy.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Modules */}
      <Card className="p-5 border border-slate-200 rounded-xl">
        <div className="flex items-center gap-2 mb-3"><Layers className="h-4 w-4 text-slate-700" /><h2 className="heading text-base font-bold text-slate-900">Module Access</h2><Badge variant="outline" className="text-[10px]">{modules.length}/{taxonomy.modules.length}</Badge></div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {taxonomy.modules.map((m) => (
            <label key={m} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${modules.includes(m) ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"}`} data-testid={`mod-${m}`}>
              <Checkbox checked={modules.includes(m)} onCheckedChange={() => toggleMod(m)} className={modules.includes(m) ? "border-white/70 data-[state=checked]:bg-white data-[state=checked]:text-slate-900" : ""} />
              <span className="text-xs font-medium capitalize">{m.replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* Actions */}
      <Card className="p-5 border border-slate-200 rounded-xl">
        <div className="flex items-center gap-2 mb-3"><Zap className="h-4 w-4 text-slate-700" /><h2 className="heading text-base font-bold text-slate-900">Action Permissions</h2><Badge variant="outline" className="text-[10px]">{actions.length}/{taxonomy.actions.length}</Badge></div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {taxonomy.actions.map((a) => (
            <label key={a} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${actions.includes(a) ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"}`} data-testid={`act-${a}`}>
              <Checkbox checked={actions.includes(a)} onCheckedChange={() => toggleAct(a)} className={actions.includes(a) ? "border-white/70 data-[state=checked]:bg-white data-[state=checked]:text-emerald-700" : ""} />
              <span className="text-xs font-medium">{a.replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* Territory */}
      <Card className="p-5 border border-slate-200 rounded-xl">
        <div className="flex items-center gap-2 mb-3"><MapPinned className="h-4 w-4 text-slate-700" /><h2 className="heading text-base font-bold text-slate-900">Territory</h2><span className="text-[10px] uppercase tracking-wider text-slate-500">primary visibility control</span></div>
        <p className="text-xs text-slate-500 mb-3">Distributors and orders the user can see. Leave empty = legacy region-based scoping (sales users fall back to their region_id).</p>
        <div className="space-y-3">
          <DistrictPicker taxonomy={taxonomy} selected={districts} onToggle={toggleDist} onClear={() => setDistricts([])} />
          {districts.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="selected-districts">
              {districts.map((d) => (
                <button key={d} onClick={() => toggleDist(d)} className="text-[11px] px-2 py-0.5 rounded-full border bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100 transition-colors">{d} ✕</button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Price list region */}
      <Card className="p-5 border border-slate-200 rounded-xl">
        <div className="flex items-center gap-2 mb-3"><Wallet className="h-4 w-4 text-slate-700" /><h2 className="heading text-base font-bold text-slate-900">Price List Region</h2><span className="text-[10px] uppercase tracking-wider text-slate-500">controls pricing in New PFI</span></div>
        <p className="text-xs text-slate-500 mb-3">Which region's price list the user uses when creating a PFI. Intentionally separate from territory (visibility).</p>
        <Select value={priceRegion || "__none__"} onValueChange={(v) => setPriceRegion(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="price-region-select" className="max-w-sm"><SelectValue placeholder="— None (user cannot create PFIs until set)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None</SelectItem>
            {taxonomy.regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} {r.code ? `(${r.code})` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {/* Commercial visibility */}
      <Card className="p-5 border border-slate-200 rounded-xl">
        <div className="flex items-center gap-2 mb-3"><Eye className="h-4 w-4 text-slate-700" /><h2 className="heading text-base font-bold text-slate-900">Commercial Visibility</h2><span className="text-[10px] uppercase tracking-wider text-slate-500">controls what ₹ / GST / Payment the user sees</span></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: "show_prices",          label: "Show Prices",          hint: "items[].rate, line amounts" },
            { key: "show_totals",          label: "Show Totals",          hint: "gross, discount, taxable, grand total" },
            { key: "show_gst",             label: "Show GST",             hint: "CGST / SGST / IGST breakdown" },
            { key: "show_payment_status",  label: "Show Payment Status",  hint: "payment_status field + by_payment dashboard block" },
          ].map((c) => (
            <label key={c.key} className="flex items-start justify-between gap-3 px-4 py-3 rounded-lg border border-slate-200 bg-white cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900">{c.label}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{c.hint}</div>
              </div>
              <Switch checked={!!cv[c.key]} onCheckedChange={(v) => setCv({ ...cv, [c.key]: v })} data-testid={`cv-${c.key}`} />
            </label>
          ))}
        </div>
      </Card>

      {/* Sticky bottom action bar for long forms */}
      <div className="sticky bottom-4 flex justify-end" >
        <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-slate-900 shadow-xl shadow-slate-900/10">
          <span className="text-[11px] uppercase tracking-wider text-slate-400">{dirty ? "Unsaved" : "Saved"}</span>
          <Button onClick={save} disabled={!dirty || saving} className="bg-white text-slate-900 hover:bg-slate-100">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}


/** Searchable district multi-select. Reads districts list from taxonomy. */
function DistrictPicker({ taxonomy, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = taxonomy.districts.filter((d) => !q || d.name.toLowerCase().includes(q.toLowerCase())).slice(0, 200);
  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="text-sm font-normal" data-testid="district-picker-open">
            <MapPinned className="h-3.5 w-3.5 mr-1.5" />
            {selected.length === 0 ? "Add Districts…" : `${selected.length} district${selected.length > 1 ? "s" : ""} selected`}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[420px] p-0">
          <div className="p-2 border-b border-slate-200">
            <Input placeholder="Search districts…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="district-search" />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-500 text-center">No districts match "{q}"</div>
            ) : filtered.map((d) => (
              <button
                key={d.name}
                onClick={() => onToggle(d.name)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm transition-colors ${selected.includes(d.name) ? "bg-blue-50 text-blue-900" : "hover:bg-slate-50 text-slate-700"}`}
                data-testid={`district-opt-${d.name}`}
              >
                <span className="flex items-center gap-2">
                  <Checkbox checked={selected.includes(d.name)} />
                  <span>{d.name}</span>
                </span>
                <span className="text-[10px] text-slate-400">{d.count}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <Button variant="ghost" size="sm" onClick={onClear} className="text-xs text-slate-500 hover:text-red-600" data-testid="clear-districts">Clear all</Button>
      )}
    </div>
  );
}
